// The AI Companion's Sales app cards (rep-app W6): the cart lines a client
// action proposes (decision D19: the shell applies them to its local cart and
// the order is sent from the cart as usual), and the follow-up email draft the
// rep edits and sends from their own mail app or the share sheet (the server
// sends nothing on its own). Styled with the chat's own tokens.
import { FormError } from '@/components/ui/FormError';
import { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { shareText } from '@/shared/platform/share';
import type { ClientActionProposal, EmailDraft } from './host';

const fieldCls = 'rounded-md border border-border bg-surface px-2 text-[12.5px] text-primary-text w-full';

export function CartLinesCard({ p, busy, err, onApprove, onDismiss }: {
  p: ClientActionProposal; busy: boolean; err?: string; onApprove: () => void; onDismiss: () => void;
}) {
  return (
    <div className="rounded-xl border border-primary/30 bg-primary/5 p-3 text-[12.5px]">
      <div className="font-semibold text-primary-text">{p.title || 'Add to the cart'}</div>
      <div className="text-[12px] text-secondary mb-2">{p.customer.name}</div>
      <ul className="space-y-1">
        {p.lines.map((l) => (
          <li key={l.item_id} className="flex items-baseline gap-2">
            <span className="font-numeric tabular-nums text-primary-text shrink-0">{`${l.qty} x ${l.code}`}</span>
            <span className="text-secondary min-w-0 truncate">{l.name}</span>
          </li>
        ))}
      </ul>
      <FormError className="text-rose-600 my-2">{err}</FormError>
      <div className="flex gap-2 mt-2.5">
        <Button size="sm" onClick={onApprove} loading={busy}>Add to cart</Button>
        <Button size="sm" variant="ghost" onClick={onDismiss} disabled={busy}>Dismiss</Button>
      </div>
      <div className="text-[11px] text-muted mt-1.5">Adds the lines to this customer&apos;s cart at their prices. You send the order from the cart.</div>
    </div>
  );
}

// mailto: with the address readable (an encoded @ confuses some mail apps)
// and the subject and body encoded.
export function mailtoHref(to: string, subject: string, body: string): string {
  const addr = encodeURIComponent(to.trim()).replace(/%40/g, '@').replace(/%2C/gi, ',');
  return `mailto:${addr}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

export function EmailDraftCard({ draft }: { draft: EmailDraft }) {
  const [to, setTo] = useState(draft.to || '');
  const [subject, setSubject] = useState(draft.subject || '');
  const [body, setBody] = useState(draft.body || '');
  const [shared, setShared] = useState<string | null>(null);
  async function share() {
    const r = await shareText({ title: subject, text: body });
    setShared(r === 'copied' ? 'Copied to the clipboard.' : r === 'unavailable' ? 'Sharing is not available here.' : null);
  }
  return (
    <div className="rounded-xl border border-primary/30 bg-primary/5 p-3 text-[12.5px] space-y-2">
      <div className="font-semibold text-primary-text">Follow-up email{draft.customer ? ` to ${draft.customer.name}` : ''}</div>
      <label className="flex flex-col gap-0.5"><span className="text-[11px] text-secondary">To</span>
        <input aria-label="To" type="email" value={to} onChange={(e) => setTo(e.target.value)} className={`${fieldCls} h-8`} /></label>
      <label className="flex flex-col gap-0.5"><span className="text-[11px] text-secondary">Subject</span>
        <input aria-label="Subject" value={subject} onChange={(e) => setSubject(e.target.value)} className={`${fieldCls} h-8`} /></label>
      <label className="flex flex-col gap-0.5"><span className="text-[11px] text-secondary">Message</span>
        <textarea aria-label="Message" value={body} onChange={(e) => setBody(e.target.value)} rows={8} className={`${fieldCls} py-1.5 leading-relaxed`} /></label>
      <div className="flex gap-2">
        <a href={mailtoHref(to, subject, body)}
          className="inline-flex items-center justify-center h-8 px-3 rounded-lg bg-primary text-white text-[12.5px] font-medium">Email</a>
        <Button size="sm" variant="ghost" onClick={() => { void share(); }}>Share</Button>
      </div>
      {shared && <div className="text-[11px] text-muted">{shared}</div>}
      <div className="text-[11px] text-muted">Opens your mail app with this text. Nothing is sent until you send it there.</div>
    </div>
  );
}
